import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
// Side-effect import: brings the `ColumnMeta` augmentation (className / width / textAlign / …)
// into scope so the `columnDef.meta` reads below are typed rather than cast.
import 'components/tables/data-table/columnMeta';
import { TableBody, TableCell, TableRow } from 'components/ui/table';
import { useMemo } from 'react';
import { cn } from 'utils/cn';
import { SIMPLE_TABLE_CELL_CLASS, SIMPLE_TABLE_ROW_CLASS } from './BasicTable';

/** Width preferences a column inherits when it declares none. This table renders no header row, so
 *  they go on every body cell's inline style. Under the auto table layout a `width` is only a hint
 *  the content can override, and `min-width: 0` sets no floor, so an undeclared column stays
 *  content-driven while still stating the same preference as its peers. */
const DEFAULT_COLUMN_WIDTH = 150;
const DEFAULT_COLUMN_MIN_WIDTH = 0;

export type UltraBasicTableProps<DataType extends object> = {
    columns?: DataTableColumnDef<DataType>[];
    renderCustomTable?: (dataToDisplay: DataType[]) => void;
    data?: DataType[];
    className?: string;
};

export const UltraBasicTable = <DataType extends object>(props: UltraBasicTableProps<DataType>) => {
    const { columns = [], data, className } = props;
    const tableContent = useMemo<DataType[]>(() => {
        return data ? (Object.values(data).flat() as DataType[]) : [];
    }, [data]);

    // react-hooks-js/incompatible-library: `useReactTable` returns an instance whose methods are
    // functions the React Compiler cannot memoize, so it skips compiling this component. Inherent
    // to the API (not refactorable), and nothing here relies on the compiler — this table renders a
    // handful of rows with no windowing or geometry engine behind it.
    // eslint-disable-next-line react-hooks-js/incompatible-library -- TanStack Table returns non-memoizable functions
    const table = useReactTable<DataType>({
        columns,
        data: tableContent,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <table className={cn('w-full text-left text-sm', className)}>
            <TableBody>
                {table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id} className={SIMPLE_TABLE_ROW_CLASS}>
                        {row.getVisibleCells().map((cell) => {
                            const meta = cell.column.columnDef.meta;
                            return (
                                <TableCell
                                    key={cell.id}
                                    className={cn(SIMPLE_TABLE_CELL_CLASS, meta?.className)}
                                    style={{
                                        textAlign: meta?.textAlign,
                                        minWidth: meta?.minWidth ?? DEFAULT_COLUMN_MIN_WIDTH,
                                        width: meta?.width ?? DEFAULT_COLUMN_WIDTH,
                                    }}
                                >
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </TableCell>
                            );
                        })}
                    </TableRow>
                ))}
            </TableBody>
        </table>
    );
};
